<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const isEditing = ref(false);
const editSessionKey = ref(0);
const contactData = ref({});
const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const SOCIAL_ICONS = {
  linkedin: 'i-ri-linkedin-box-fill',
  facebook: 'i-ri-facebook-circle-fill',
  instagram: 'i-ri-instagram-line',
  telegram: 'i-ri-telegram-fill',
  tiktok: 'i-ri-tiktok-fill',
  twitter: 'i-ri-twitter-x-fill',
  github: 'i-ri-github-fill',
};

const detailFields = computed(() => {
  const {
    email,
    phoneNumber,
    additionalAttributes = {},
  } = props.selectedContact || {};
  const { city, country, companyName, description } =
    additionalAttributes || {};

  return [
    { key: 'EMAIL', value: email },
    { key: 'PHONE', value: phoneNumber },
    { key: 'CITY', value: city },
    { key: 'COUNTRY', value: country },
    { key: 'COMPANY', value: companyName },
    { key: 'BIO', value: description, fullWidth: true },
  ];
});

const socialProfiles = computed(() => {
  const { socialProfiles: profiles = {}, socialTelegramUserName } =
    props.selectedContact?.additionalAttributes || {};

  return Object.entries(SOCIAL_ICONS)
    .map(([key, icon]) => ({
      key,
      icon,
      handle:
        profiles?.[key] || (key === 'telegram' ? socialTelegramUserName : ''),
    }))
    .filter(({ handle }) => Boolean(handle));
});

const startEditing = () => {
  contactData.value = { ...props.selectedContact };
  editSessionKey.value += 1;
  isEditing.value = true;
};

const cancelEditing = () => {
  isEditing.value = false;
};

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const saveContact = async () => {
  try {
    // contactType is owned by the lifecycle stepper, and lifecycle events /
    // Captain provenance are server-managed; sending the form's snapshot of
    // any of them back would clobber changes made while the form was open
    const { customAttributes, contactType, additionalAttributes, ...rest } =
      contactData.value;
    const { lifecycleEvents, captainFilledAttributes, ...editableAttributes } =
      additionalAttributes || {};
    await store.dispatch('contacts/update', {
      ...rest,
      additionalAttributes: editableAttributes,
    });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
    isEditing.value = false;
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <CardLayout>
    <div class="flex items-center justify-between gap-3">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.ABOUT.TITLE') }}
      </h4>
      <Button
        v-if="!isEditing"
        :label="t('CONTACTS_LAYOUT.DETAILS.ABOUT.EDIT')"
        icon="i-lucide-pencil"
        size="sm"
        variant="ghost"
        color="slate"
        @click="startEditing"
      />
      <div v-else class="flex items-center gap-2">
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.ABOUT.CANCEL')"
          size="sm"
          variant="ghost"
          color="slate"
          :disabled="isUpdating"
          @click="cancelEditing"
        />
        <Button
          :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
          size="sm"
          :is-loading="isUpdating"
          :disabled="isUpdating || isFormInvalid"
          @click="saveContact"
        />
      </div>
    </div>

    <div v-if="!isEditing" class="flex flex-col gap-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        <div
          v-for="field in detailFields"
          :key="field.key"
          class="flex items-baseline min-w-0 gap-3 py-1"
          :class="{ 'sm:col-span-2': field.fullWidth }"
        >
          <span class="w-24 text-sm shrink-0 text-n-slate-11">
            {{ t(`CONTACTS_LAYOUT.DETAILS.ABOUT.FIELDS.${field.key}`) }}
          </span>
          <span
            v-if="field.value"
            class="min-w-0 text-sm break-words text-n-slate-12"
          >
            {{ field.value }}
          </span>
          <span v-else class="text-sm text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.DETAILS.ABOUT.EMPTY_VALUE') }}
          </span>
        </div>
      </div>
      <div
        v-if="socialProfiles.length"
        class="flex items-baseline gap-3 pt-3 border-t border-n-weak"
      >
        <span class="w-24 text-sm shrink-0 text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.DETAILS.ABOUT.FIELDS.SOCIAL') }}
        </span>
        <div class="flex flex-wrap items-center gap-2">
          <span
            v-for="profile in socialProfiles"
            :key="profile.key"
            class="inline-flex items-center gap-1.5 px-2 h-7 rounded-lg bg-n-alpha-2 text-sm text-n-slate-12"
          >
            <Icon :icon="profile.icon" class="size-4 text-n-slate-11" />
            {{ profile.handle }}
          </span>
        </div>
      </div>
    </div>

    <ContactsForm
      v-else
      :key="editSessionKey"
      ref="contactsFormRef"
      :contact-data="contactData"
      is-details-view
      @update="handleFormUpdate"
    />
  </CardLayout>
</template>
